import React, { useEffect, useRef, useState } from 'react';

// Score per session, oldest → newest. One series, so no legend: the card title
// names it. 2px line, 10% area wash, end value labelled, crosshair + tooltip on
// hover/tap. Colour validated against the dark surface (dataviz validator).
const LINE = '#3387FF';
const SURFACE = '#0B0F1C';
const H = 180;
const PAD = { top: 16, right: 44, bottom: 24, left: 28 };

const TrendChart = ({ history }) => {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(260, e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!history || history.length < 2) {
    return <p className="py-10 text-center text-sm text-white/45">Your trend appears after two sessions.</p>;
  }

  const scores = history.map((h) => h.score);
  const lo = Math.max(0, Math.min(50, Math.floor((Math.min(...scores) - 5) / 10) * 10));
  const innerW = width - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const x = (i) => PAD.left + (history.length === 1 ? innerW / 2 : (i / (history.length - 1)) * innerW);
  const y = (v) => PAD.top + (1 - (v - lo) / (100 - lo)) * innerH;
  const pts = history.map((h, i) => [x(i), y(h.score)]);
  const line = pts.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${PAD.top + innerH} L${pts[0][0].toFixed(1)},${PAD.top + innerH} Z`;
  const ticks = [lo, Math.round((lo + 100) / 2 / 5) * 5, 100];
  const last = history.length - 1;
  const step = innerW / Math.max(1, history.length - 1);
  const active = hover ?? null;

  return (
    <div ref={wrapRef} className="relative w-full select-none">
      <svg width={width} height={H} role="img" aria-label={`Score trend over ${history.length} sessions, latest ${Math.round(scores[last])}`}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={PAD.left + innerW} y1={y(t)} y2={y(t)} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="fill-white/35 text-[10px]">{t}</text>
          </g>
        ))}
        <path d={area} fill={LINE} opacity="0.1" />
        <path d={line} fill="none" stroke={LINE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {active != null && (
          <line x1={pts[active][0]} x2={pts[active][0]} y1={PAD.top} y2={PAD.top + innerH} stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
        )}
        {[active ?? last].map((i) => (
          <circle key={i} cx={pts[i][0]} cy={pts[i][1]} r="4.5" fill={LINE} stroke={SURFACE} strokeWidth="2" />
        ))}
        <text x={pts[last][0] + 10} y={pts[last][1] + 4} className="fill-white text-[12px] font-semibold">{Math.round(scores[last])}</text>
        <text x={PAD.left} y={H - 6} className="fill-white/35 text-[10px]">{history[0].date}</text>
        <text x={PAD.left + innerW} y={H - 6} textAnchor="end" className="fill-white/35 text-[10px]">{history[last].date}</text>
        {/* hit targets wider than the marks */}
        {pts.map(([px], i) => (
          <rect
            key={i}
            x={px - step / 2}
            y={PAD.top}
            width={Math.max(step, 12)}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            onClick={() => setHover(i)}
          />
        ))}
      </svg>
      {active != null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-white/10 bg-ink-800 px-2.5 py-1.5 text-xs shadow-xl"
          style={{ left: Math.min(Math.max(pts[active][0], 60), width - 60), top: pts[active][1] - 10 }}
        >
          <p className="font-semibold text-white">{Math.round(scores[active])} / 100</p>
          <p className="text-white/50">{history[active].date} · session {active + 1}</p>
        </div>
      )}
    </div>
  );
};

export default TrendChart;
